import { FilterType, MetadataTypeId, PageParameterId } from '@middleware/types';

// ============================================================================
// Constants
// ============================================================================

export const MAX_CARDINALITY_LIMIT = 500_000;

export const MAX_GROUP_CARDINALITY = 10_000;

// ============================================================================
// Group value helpers
// ============================================================================

/**
 * Extract an object of { groupValue0, groupValue1, ... } from a data row.
 */
export function pickGroupValues (data: Record<string, unknown>): Record<string, unknown> {
    const groupObject: Record<string, unknown> = {};
    let i = 0;
    let key: string;

    while ((key = `groupValue${i++}`) in data) {
        groupObject[key] = data[key];
    }

    return groupObject;
}

/**
 * Return an ordered array of group values from a data row.
 * Recognizes falsy, undefined, and inherited properties.
 */
export function pickOrderedGroupValues (data: Record<string, unknown> | null | undefined): unknown[] {
    const groupArray: unknown[] = [];
    let i = 0;
    let key: string;

    if (data) {
        while ((key = `groupValue${i++}`) in data) {
            groupArray.push(data[key]);
        }
    }

    return groupArray;
}

// ============================================================================
// Filter identifier
// ============================================================================

/**
 * Produce a canonical string identifier for a filter, used to key into
 * the resolved filter properties map.
 */
export function getFilterIdentifier ({
    field,
    filterType,
    kind,
    group = '',
}: {
    field: string;
    filterType: FilterType;
    kind?: MetadataTypeId | string;
    group?: string;
}): string {
    if (filterType === FilterType.Visitor && field === 'id') {
        return 'visitorId';
    }
    if (filterType === FilterType.Account && field === 'id') {
        return 'accountId';
    }

    if (
        filterType === FilterType.ParentAccount ||
        filterType === FilterType.Account ||
        filterType === FilterType.Visitor
    ) {
        return `${kind}.${group}.${field}`;
    }

    if (filterType === FilterType.Location && field === 'region') return 'country_region';
    if (filterType === FilterType.PageParameter) return PageParameterId;
    if (filterType === FilterType.EventTopLevel || filterType === FilterType.Location) return `${field}`;

    return `${field}_${filterType}_${kind}`;
}

// ============================================================================
// Date config helpers
// ============================================================================

/**
 * Compute the number of days in a date config range.
 * Returns null if the date config has no range type.
 */
export function retrieveDayCountFromDateConfig (dateConfig: {
    value: { dateRange: { type: string; start?: string | number; end?: string | number } };
}): number | null {
    const range = dateConfig?.value?.dateRange?.type;
    if (!range) {
        return null;
    }

    if (range === 'Custom') {
        const { start, end } = dateConfig.value.dateRange;
        if (start == null || end == null) return null;
        // Compute day diff from epoch milliseconds or date strings
        const startMs = typeof start === 'number' ? start : new Date(start).getTime();
        const endMs = typeof end === 'number' ? end : new Date(end).getTime();

        return Math.round((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1;
    }

    if (['Today', 'Yesterday'].includes(range)) {
        return 1;
    }

    const match = /(Same|Previous|Last) (\d*) (Day(s)? Last Year|Day(s)?)/.exec(range);

    return match ? parseInt(match[2], 10) : null;
}
